import { format } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';

import { noshowAlertsAPI } from '@/services/noshowAlertsAPI';
import { NoshowSmsMessage } from '@/types/noshowAlerts';

import Alert from '@/components/Alert';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Body, Caption } from '@/components/Typography';
import styled from '@/styles';

interface SmsConversationProps {
  alertId: number;
  onClose: () => void;
}

const ScrollContainer = styled('div', {
  maxHeight: '400px',
  overflowY: 'auto',
  minHeight: '120px',
});

const MessageRow = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  variants: {
    direction: {
      outbound: { maxWidth: '100%', width: '100%', alignItems: 'flex-end' },
      inbound: { alignItems: 'flex-start' },
    },
  },
});

const MessageBubble = styled('div', {
  padding: '8px 12px',
  borderRadius: '12px',
  variants: {
    direction: {
      outbound: {
        maxWidth: '50%',
        backgroundColor: '$infoDefault',
        borderBottomRightRadius: '4px',
      },
      inbound: {
        maxWidth: '80%',
        backgroundColor: '$neutralDefault',
        color: 'white',
        borderBottomLeftRadius: '4px',
      },
    },
  },
});

const MESSAGE_TYPE_LABELS: Record<string, string> = {
  presence_check: 'Presence Check',
  presence_response: 'Response',
  reminder: 'Reminder',
  backfill_question: 'Backfill Question',
  backfill_response: 'Response',
  confirmation: 'Confirmation',
  invalid: 'Invalid',
  invalid_prompt: 'Retry Prompt',
};

const SmsConversation = ({ alertId, onClose }: SmsConversationProps) => {
  const [messages, setMessages] = useState<NoshowSmsMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await noshowAlertsAPI.getMessages(alertId);
      if (response.success) {
        setMessages(response.data || []);
      }
    } catch (err: any) {
      console.error('[SmsConversation] Error:', err);
      setError('Failed to load SMS messages');
    } finally {
      setLoading(false);
    }
  }, [alertId]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (!loading && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [loading, messages]);

  return (
    <Modal onRequestClose={onClose} title={`SMS Thread — Alert #${alertId}`}>
      <Card.Section>
        {error && (
          <Alert
            description={error}
            icon="warning"
            status="danger"
            title="Error"
          />
        )}
        <ScrollContainer ref={scrollRef}>
          {loading && <LoadingState overlayColor="white" />}
          {!loading && messages.length === 0 && !error && (
            <Body color="light">No messages recorded yet.</Body>
          )}
          {!loading && (
            <Stack vertical gap={12}>
              {messages.map((msg) => (
                <MessageRow
                  key={msg.id}
                  direction={
                    msg.direction === 'outbound' ? 'outbound' : 'inbound'
                  }
                >
                  <MessageBubble
                    direction={
                      msg.direction === 'outbound' ? 'outbound' : 'inbound'
                    }
                  >
                    <Body
                      color={
                        msg.direction === 'inbound' ? 'hi-contrast' : undefined
                      }
                    >
                      {msg.body}
                    </Body>
                  </MessageBubble>
                  <Caption color="lighter" css={{ marginTop: '2px' }}>
                    {msg.direction === 'outbound' ? 'Sent' : 'Received'}{' '}
                    {format(new Date(msg.created_at), 'MMM dd, h:mm a')}
                    {msg.message_type &&
                      ` · ${MESSAGE_TYPE_LABELS[msg.message_type] || msg.message_type}`}
                  </Caption>
                </MessageRow>
              ))}
            </Stack>
          )}
        </ScrollContainer>
      </Card.Section>
    </Modal>
  );
};

export default SmsConversation;
